import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { setCommunityData, setPropertyData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { useGetCommunities, useGetTncAcceptanceLogs, useGetUserById, useGetUserRelationships } from "@/services";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

const DATE = "MM/DD/YYYY";
const DATE_TIME = "MM/DD/YYYY h:mm A";

export const formatDate = (value?: string, format = DATE) =>
  value ? moment(value).format(format) : "—";

export const formatAddress = (address?: any) => {
  if (!address) return "";
  const poBox = address.pobox ? `PO Box ${address.pobox}` : "";
  return [address.addressLine1, address.addressLine2, poBox, address.city, address.state, address.zipCode]
    .filter((part) => part && String(part).trim())
    .join(", ");
};

const humanize = (value?: string) => (value ? value.replace(/_/g, " ") : "—");

const useOwnerDetails = () => {
  const navigate = useNavigate();
  const { userId = "" } = useParams();
  const isAdmin = location.pathname.startsWith("/admin");
  const [openResetDialog, setOpenResetDialog] = useState<any>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  // PUT /users/{id} is ADMIN/CAM; CAM additionally needs MASTER_DATA update
  const canEdit = isAdmin || (location.pathname.startsWith("/cam") && !!masterDataPermission?.update);

  const { data: userDetails, isLoading: isUserLoading } = useGetUserById({ id: userId });
  const { data: relationshipData, isLoading: isRelationshipsLoading } = useGetUserRelationships({ userId });
  const { data: termsData, isLoading: isTermsLoading, isError: isTermsError } = useGetTncAcceptanceLogs({
    userId,
    queryParams: { enabled: !!userId },
  });
  const termsAcceptanceLogs = termsData?.data || [];
  const latestTermsAcceptance = [...termsAcceptanceLogs].sort(
    (a: any, b: any) => moment(b.acceptedAt).valueOf() - moment(a.acceptedAt).valueOf()
  )[0];
  const termsColumns = [
    { accessorKey: "communityName", header: "Community" },
    { accessorKey: "versionNo", header: "Version" },
    { accessorKey: "lawName", header: "Law Name" },
    {
      accessorKey: "acceptedAt",
      header: "Accepted On",
      accessorFn: (row: any) => formatDate(row.acceptedAt, DATE_TIME),
    },
  ];

  // PO link -> People > Property Ownership tab, filtered to that ownership in its community.
  // /<admin|cam>/master-data/owner/details/:userId -> /<admin|cam>/master-data/property-ownership
  const openInPropertyOwnership = (ownershipId: number, communityId: number) => {
    const base = location.pathname.replace(/\/owner\/details\/.*$/, "");
    navigate(`${base}/property-ownership?ownershipId=${ownershipId}&communityId=${communityId}`);
  };

  // Property link -> Property Config > Property Management > property page. That page reads the
  // selected community, and relationships can be in other communities, so switch to it first.
  const dispatch = useDispatch();
  const { data: communityList } = useGetCommunities({});
  const openProperty = (propertyId: number, propertyName: string, communityId: number) => {
    const community = communityList?.data?.find((c: any) => c.communityId == communityId);
    if (community) dispatch(setCommunityData(community));
    dispatch(setPropertyData({ propertyId, propertyName }));
    const base = location.pathname.replace(/\/owner\/details\/.*$/, "");
    navigate(`${base}/properties/${propertyId}/view`);
  };

  const user = userDetails?.data;
  const relationships = relationshipData?.data || [];

  // Same rule as the Users grid (owner_master_grid_view.user_classification), over the communities the
  // caller can see: active Owner + active Tenant -> Owner / Tenant; otherwise the active one; with no active
  // role, the last held (an owner added without a property is Owner).
  const userClassification = (() => {
    const has = (role: string, activeOnly: boolean) =>
      relationships.some((r: any) => r.relationship === role && (!activeOnly || r.active));
    const owner = has("OWNER", true), tenant = has("TENANT", true);
    if (owner && tenant) return "Owner / Tenant";
    if (owner) return "Owner";
    if (tenant) return "Tenant";
    return has("TENANT", false) && !has("OWNER", false) ? "Tenant" : "Owner";
  })();

  const accountSecurity = (() => {
    if (!user) return "—";
    if (user.accountLocked) return "Locked";
    if (user.forcePasswordChange) return "Password change required";
    if (user.passwordExpiry && moment(user.passwordExpiry).isBefore(moment())) return "Password expired";
    return "In good standing";
  })();

  const columns = [
    {
      accessorKey: "ownershipId",
      header: "Property Ownership ID",
      cell: ({ row }: any) =>
        row.original.ownershipId ? (
          <button
            className="text-primary font-semibold hover:underline"
            onClick={() => openInPropertyOwnership(row.original.ownershipId, row.original.communityId)}
          >
            PO-{row.original.ownershipId}
          </button>
        ) : "—",
    },
    {
      accessorKey: "communityId",
      header: "Community ID",
    },
    {
      accessorKey: "communityName",
      header: "Community Name",
    },
    {
      accessorKey: "propertyName",
      header: "Property",
      cell: ({ row }: any) => (
        <button
          className="text-primary hover:underline text-left"
          onClick={() => openProperty(row.original.propertyId, row.original.propertyName, row.original.communityId)}
        >
          {row.original.propertyName || `Property ${row.original.propertyId}`}
        </button>
      ),
    },
    {
      accessorKey: "relationship",
      header: "Relationship",
      cell: ({ row }: any) => (
        <Badge variant="outline" className="rounded-md px-2 py-0.5 text-xs font-semibold">
          {humanize(row.original.relationship)}
        </Badge>
      ),
    },
    {
      accessorKey: "occupancyType",
      header: "Occupancy Type",
      accessorFn: (row: any) => humanize(row?.occupancyType),
    },
    {
      accessorKey: "sharePercentage",
      header: "Title Share",
      accessorFn: (row: any) =>
        row?.sharePercentage != null ? `${Number(row.sharePercentage)}%` : "—",
    },
    {
      accessorKey: "primary",
      header: "Primary",
      accessorFn: (row: any) => (row?.primary ? "Yes" : "No"),
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      accessorFn: (row: any) => formatDate(row?.startDate),
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      accessorFn: (row: any) => formatDate(row?.endDate),
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ row }: any) => (
        <Badge
          variant="outline"
          className={cn(
            row.original.active
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-gray-200 bg-gray-100 text-gray-600",
            "rounded-md px-2 py-0.5"
          )}
        >
          {row.original.active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  return {
    user,
    relationships,
    userClassification,
    columns,
    accountSecurity,
    isLoading: isUserLoading || isRelationshipsLoading,
    isAdmin,
    canEdit,
    isEditing,
    setIsEditing,
    openResetDialog,
    openTermsDialog,
    setOpenTermsDialog,
    termsAcceptanceLogs,
    latestTermsAcceptance,
    termsColumns,
    isTermsLoading,
    isTermsError,
    setOpenResetDialog,
    onBack: () => navigate("../..", { relative: "path" }),
    formatDate,
    DATE_TIME,
  };
};

export default useOwnerDetails;
